import type {
  Change,
  ChangeSummary,
  HistoryItem,
  OrgSnapshot,
  OrgStats,
  Scenario,
  ScenarioFile,
  ScenarioOp,
  ScenarioSummary,
  Squad,
  SquadRole,
} from '@tive/core';
import { authHeaders, expireSession } from './auth';

/** Identifies this browser tab so we can tell our own echoes from Claude's edits. */
export const clientId = `web-${Math.random().toString(36).slice(2, 8)}`;

/**
 * A scenario in the list, plus what this person may do with it.
 *
 * The role is Drive's, carried through rather than decided here — a plan
 * shared for viewing has to look read-only before the first edit, not after
 * one is refused.
 */
export type SharedSummary = ScenarioSummary & { role?: 'owner' | 'writer' | 'reader' | null };

/** One person with a scenario open right now. Not persisted; a closed tab is nobody. */
export interface Viewer {
  id: string;
  name: string;
  email: string | null;
  /** What Drive says they may do with the open plan, where it has said. */
  role?: 'owner' | 'writer' | 'reader';
  isClaude?: boolean;
}

export interface OrgMeta {
  syncedAt: string;
  source: string;
  statuses: string[];
  headcount: number;
  fromSeed: boolean;
  ageDays: number;
}

export interface OrgResponse {
  snapshot: OrgSnapshot;
  meta: OrgMeta;
  stats: OrgStats;
  snowflakeConfigured: boolean;
}

export interface ScenarioPayload {
  scenario: Scenario;
  changes: Change[];
  summary: ChangeSummary;
  warnings: string[];
  messages?: string[];
  importWarnings?: string[];
  /** Sent back by undo and redo, so the panel updates without a second request. */
  history?: HistoryPayload;
}

/**
 * What comes back when nobody has said which scenario yet. The scenario is
 * nullable here and nowhere else: on a first run there isn't one, and the app
 * asks rather than inventing "Scenario 1" in somebody's Drive.
 */
export type ActiveScenarioPayload = Omit<ScenarioPayload, 'scenario'> & {
  scenario: Scenario | null;
};

/** What the server says about itself before anyone has signed in. */
export interface HealthPayload {
  ok: boolean;
  snowflakeConfigured: boolean;
  /** True once GOOGLE_CLIENT_ID is set on the server. */
  authRequired: boolean;
  /** The OAuth client to sign in against, so the SPA ships no configuration. */
  clientId: string | null;
  /**
   * Browser key for Google's file picker. Null when there is nothing to pick
   * from, which is every local run — and the picker is the only way to open a
   * plan somebody shared, because `drive.file` cannot see a file until it has
   * been picked.
   */
  pickerApiKey: string | null;
  /** Cloud project number. Without it, picking a file grants the app nothing. */
  pickerAppId: string | null;
  /**
   * Which release the server is running. A tab keeps the one it started on and
   * compares, which is how it knows it is looking at the previous version.
   */
  buildId: string;
  /** Whether a sign-in lasts a working day or only as long as Google`s token. */
  sessions: boolean;
  /** Whether the server can make a Google Sheet — which needs a signed-in account. */
  sheets: boolean;
  version: number;
}

export interface HistoryPayload {
  items: HistoryItem[];
  canUndo: boolean;
  canRedo: boolean;
}

export interface SquadPayload {
  squads: Squad[];
  updatedAt: string;
  /** Memberships dropped because the person is no longer in the org. */
  warnings: string[];
  /** True when the base teams are the published ones and cannot be edited here. */
  shared?: boolean;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /**
     * Set for the failures the UI answers rather than reports. `read-only` is
     * the one that matters: somebody with view access tried to change a plan,
     * and what they want next is a copy of their own.
     */
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      // Every request carries the session when there is one; hosted, the API
      // refuses anything that does not.
      headers: {
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...authHeaders(),
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiError('Cannot reach the local API server. Is `npm run dev` still running?', 0);
  }

  if (!response.ok) {
    /*
     * The token has aged out — they last about an hour and nothing here can
     * renew one. Dropping the session puts the sign-in screen back up, which is
     * the actual answer; without this every request in flight turns into its own
     * error toast and the app looks broken rather than signed out.
     */
    if (response.status === 401) expireSession();

    let message = `${response.status} ${response.statusText}`;
    let code: string | undefined;
    try {
      const body = (await response.json()) as { error?: string; code?: string };
      if (body.error) message = body.error;
      code = body.code;
    } catch {
      /* keep the status text */
    }
    throw new ApiError(message, response.status, code);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

export const api = {
  org: () => request<OrgResponse>('/api/org'),

  /**
   * Hand a grid to the server to become a spreadsheet.
   *
   * The rows are built here, where the columns somebody chose already live;
   * the server only carries them to Google.
   */
  exportSheet: (title: string, grid: string[][]) =>
    post<{ id: string; url: string }>('/api/export/sheet', { title, grid }),
  refreshOrg: () => post<{ snapshot: OrgSnapshot; meta: OrgMeta }>('/api/org/refresh'),

  listScenarios: () => request<{ scenarios: SharedSummary[]; activeId: string | null }>('/api/scenarios'),
  activeScenario: () => request<ActiveScenarioPayload>('/api/scenarios/active'),
  getScenario: (id: string) => request<ScenarioPayload>(`/api/scenarios/${encodeURIComponent(id)}`),
  setActiveScenario: (scenarioId: string | null) =>
    request<ActiveScenarioPayload>('/api/scenarios/active', {
      method: 'PUT',
      body: JSON.stringify({ scenarioId, origin: clientId }),
    }),
  createScenario: (name: string, description?: string) =>
    post<ScenarioPayload>('/api/scenarios', { name, description, origin: clientId }),
  renameScenario: (id: string, patch: { name?: string; description?: string }) =>
    request<ScenarioPayload>(`/api/scenarios/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  duplicateScenario: (id: string, name?: string) =>
    post<ScenarioPayload>(`/api/scenarios/${id}/duplicate`, { name }),
  deleteScenario: (id: string) =>
    request<{ ok: true; activeId: string | null }>(`/api/scenarios/${id}`, { method: 'DELETE' }),

  applyOps: (id: string, ops: ScenarioOp[]) =>
    post<ScenarioPayload>(`/api/scenarios/${id}/ops`, { ops, origin: clientId }),

  health: () => request<HealthPayload>('/api/health'),

  /**
   * Where this scenario lives, so "Share" can hand it to Drive's own dialog.
   * Null when scenarios are files on disk, which is every local run.
   */
  shareLink: (id: string) =>
    request<{ url: string | null }>(`/api/scenarios/${encodeURIComponent(id)}/share`),

  history: (id: string) => request<HistoryPayload>(`/api/scenarios/${id}/history`),
  undo: (id: string) => post<ScenarioPayload>(`/api/scenarios/${id}/undo`, { origin: clientId }),
  redo: (id: string) => post<ScenarioPayload>(`/api/scenarios/${id}/redo`, { origin: clientId }),

  exportScenario: (id: string) => request<ScenarioFile>(`/api/scenarios/${id}/export`),
  importScenario: (file: unknown) =>
    post<ScenarioPayload>('/api/scenarios/import', { file, origin: clientId }),
  /** A scenario Drive handed us, by file id — see `driveOpenFileId` in lib/tab. */
  openDriveFile: (fileId: string) =>
    post<ScenarioPayload>('/api/scenarios/open-drive-file', { fileId, origin: clientId }),

  // Squads are shared by every scenario, so none of these take a scenario id.
  listSquads: () => request<SquadPayload>('/api/squads'),
  createSquad: (name: string, description?: string, parentId?: string | null) =>
    post<SquadPayload & { squad: Squad }>('/api/squads', {
      name,
      description,
      parentId,
      origin: clientId,
    }),
  updateSquad: (
    id: string,
    patch: { name?: string; description?: string; parentId?: string | null; color?: string | null },
  ) =>
    request<SquadPayload>(`/api/squads/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...patch, origin: clientId }),
    }),
  deleteSquad: (id: string) =>
    request<SquadPayload>(`/api/squads/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** Adds someone or changes the hat they wear — the same call either way. */
  setSquadMember: (id: string, personId: string, role: SquadRole) =>
    request<SquadPayload>(`/api/squads/${encodeURIComponent(id)}/members/${encodeURIComponent(personId)}`, {
      method: 'PUT',
      body: JSON.stringify({ role, origin: clientId }),
    }),
  removeSquadMember: (id: string, personId: string) =>
    request<SquadPayload>(`/api/squads/${encodeURIComponent(id)}/members/${encodeURIComponent(personId)}`, {
      method: 'DELETE',
    }),
};
